import { useState, useMemo, useRef } from "react";
import { diatonic, chordMidis, progressions, tonicsFor, preferredTonic, pcOf } from "@/lib/theory";
import { playProgression } from "@/lib/audio";
import { progressionToMidiBlob } from "@/lib/midi";
import { useBuilder } from "@/context/BuilderContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Play, Square, Trash2, Download, Wand2, ListMusic, Save } from "lucide-react";

const CHORD_TYPES = ["Triad", "7ths", "sus2", "sus4"];
const BARS = [1, 2, 4];
const DISPLAY = { "C#": "C♯", "D#": "D♯", "F#": "F♯", "G#": "G♯", "A#": "A♯", "Db": "D♭", "Eb": "E♭", "Gb": "G♭", "Ab": "A♭", "Bb": "B♭" };

export default function ProgressionBuilder() {
  const { tonic, setTonic, mode, setMode, seq, setSeq, bpm, setBpm, bars, setBars } = useBuilder();
  const { user, openAuth } = useAuth();
  const [ctype, setCtype] = useState("Triad");
  const [playingIdx, setPlayingIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [saveMsg, setSaveMsg] = useState(null);
  const stopRef = useRef(null);

  const dia = useMemo(() => diatonic(tonic, mode), [tonic, mode]);
  const qualityFor = (c) => (ctype === "7ths" ? c.seventh : ctype === "sus2" ? "sus2" : ctype === "sus4" ? "sus4" : c.quality);
  const addChord = (c) => {
    const q = qualityFor(c);
    setSeq([...seq, { root: c.root, quality: q, label: c.root + (q === "dim" ? "dim" : q) }]);
  };
  const midisOf = (item) => chordMidis(item.root, item.quality);

  const play = () => {
    if (stopRef.current) { stopRef.current(); stopRef.current = null; setPlayingIdx(-1); setIsPlaying(false); return; }
    if (!seq.length) return;
    setIsPlaying(true);
    stopRef.current = playProgression(seq.map(midisOf), bpm, bars * 4, true, (i) => {
      setPlayingIdx(i);
      if (i === -1) { stopRef.current = null; setIsPlaying(false); }
    });
  };
  const clear = () => { if (stopRef.current) { stopRef.current(); stopRef.current = null; } setPlayingIdx(-1); setIsPlaying(false); setSeq([]); };
  const changeMode = (m) => { setTonic(preferredTonic(pcOf(tonic), m)); setMode(m); };
  const suggest = () => {
    const all = progressions(tonic, mode);
    const pick = all[Math.floor(Math.random() * all.length)];
    setSeq(pick.chords.map((c) => ({ root: c.root, quality: c.quality, label: c.name })));
  };
  const exportMidi = () => {
    if (!seq.length) return;
    const url = URL.createObjectURL(progressionToMidiBlob(seq.map(midisOf), bpm, bars * 4));
    const a = document.createElement("a");
    a.href = url; a.download = `progression-${tonic}-${mode}.mid`; a.click();
    URL.revokeObjectURL(url);
  };
  const openSave = () => {
    if (!seq.length) return;
    if (!user) { openAuth("login"); return; }
    setSaveMsg(null); setSaveOpen(true);
  };
  const doSave = async () => {
    try {
      await api.post("/progressions", { title: title || "Untitled", tonic, mode, chords: seq, bpm, bars, note, is_public: isPublic });
      setSaveOpen(false); setTitle(""); setNote("");
      setSaveMsg("Saved! Find it below in Your saved progressions.");
      window.dispatchEvent(new Event("wtk-progressions-updated"));
    } catch (e) {
      setSaveMsg(apiError(e.response?.data?.detail));
    }
  };

  return (
    <section className="wtk-builder" id="builder" data-testid="progression-builder">
      <div className="wtk-builder-inner">
        <div className="wtk-eyebrow"><ListMusic size={15} /> Progression builder</div>
        <h2 className="wtk-section-h wtk-builder-title">Build & loop your own progression</h2>

        <div className="wtk-builder-controls">
          <div className="wtk-mini-select">
            {tonicsFor(mode).map((t) => (
              <button key={t} className={`wtk-mini-chip ${tonic === t ? "on" : ""}`} data-testid={`builder-tonic-${t.replace("#", "s")}`} onClick={() => setTonic(t)}>{DISPLAY[t] || t}</button>
            ))}
          </div>
          <div className="wtk-builder-row">
            <div className="wtk-seg">{["Major", "Minor"].map((m) => (<button key={m} className={mode === m ? "on" : ""} onClick={() => changeMode(m)}>{m}</button>))}</div>
            <div className="wtk-seg" data-testid="chord-type">{CHORD_TYPES.map((t) => (<button key={t} className={ctype === t ? "on" : ""} onClick={() => setCtype(t)}>{t}</button>))}</div>
            <div className="wtk-seg">{BARS.map((b) => (<button key={b} className={bars === b ? "on" : ""} onClick={() => setBars(b)}>{b} bar{b > 1 ? "s" : ""}</button>))}</div>
            <label className="wtk-bpm"><input type="range" min="60" max="180" value={bpm} onChange={(e) => setBpm(+e.target.value)} data-testid="bpm-slider" /><span>{bpm} BPM</span></label>
          </div>
        </div>

        <div className="wtk-chords" data-testid="builder-palette">
          {dia.map((c, i) => (
            <button key={i} className="wtk-chord" data-testid={`palette-${i}`} onClick={() => addChord(c)}>
              <span className="wtk-chord-num">{c.numeral}</span>
              <span className="wtk-chord-name">{c.root + (qualityFor(c) === "dim" ? "dim" : qualityFor(c))}</span>
            </button>
          ))}
        </div>
        <p className="wtk-hint" style={{ textAlign: "left", margin: "6px 0 16px" }}>Tap chords to build your progression, then loop it.</p>

        <div className="wtk-seq" data-testid="sequence">
          {seq.length === 0 ? (
            <span className="wtk-seq-empty">Your progression will appear here…</span>
          ) : (
            seq.map((c, i) => (<span key={i} className={`wtk-seq-chip ${playingIdx === i ? "playing" : ""}`} data-testid={`seq-chip-${i}`}>{c.label}</span>))
          )}
        </div>

        <div className="wtk-builder-actions">
          <button className="wtk-analyze-btn" data-testid="builder-play" onClick={play} disabled={!seq.length}>{isPlaying ? (<><Square size={16} /> Stop</>) : (<><Play size={16} /> Loop</>)}</button>
          <button className="wtk-analyze-btn wtk-secondary" data-testid="builder-save" onClick={openSave} disabled={!seq.length}><Save size={16} /> Save</button>
          <button className="wtk-analyze-btn wtk-secondary" data-testid="builder-suggest" onClick={suggest}><Wand2 size={16} /> Suggest</button>
          <button className="wtk-analyze-btn wtk-secondary" data-testid="builder-midi" onClick={exportMidi} disabled={!seq.length}><Download size={16} /> MIDI</button>
          <button className="wtk-analyze-btn wtk-secondary" data-testid="builder-clear" onClick={clear} disabled={!seq.length}><Trash2 size={16} /> Clear</button>
        </div>
        {saveMsg && <p className="wtk-hint" data-testid="save-msg" style={{ textAlign: "left", marginTop: 12 }}>{saveMsg}</p>}

        {saveOpen && (
          <div className="wtk-modal-overlay" data-testid="save-modal" onClick={() => setSaveOpen(false)}>
            <div className="wtk-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="wtk-modal-title">Save progression</h2>
              <input className="wtk-text-input" data-testid="save-title" placeholder="Title (e.g. Dreamy chorus)" value={title} onChange={(e) => setTitle(e.target.value)} />
              <input className="wtk-text-input" data-testid="save-note" placeholder="Mood / note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <label className="wtk-check"><input type="checkbox" data-testid="save-public" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Share to the community library</label>
              <button className="wtk-analyze-btn" data-testid="save-confirm" onClick={doSave}>Save progression</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
